import { FileType as FileMimeType, } from '@debbie/common/dist/files/index.js';
import { TemplateMention, } from '@debbie/common/dist/templates/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import { successNotification } from '../../../utils/errors.js';
import { Button, CopyToClipboard, FileDropzone, Panel } from '../../index.js';
import PowerTable from '../../PowerTable.js';
const HTML_BODY_TAG = '{~~body}';
const renderTagFromField = (field) => {
    const ifMatch = field.match(/^\{\{#if\s+(.+?)\}\}\s*$/);
    if (ifMatch) {
        const variable = ifMatch[1].trim();
        return {
            kind: 'conditional',
            variable,
            truthyText: `{#${variable}}\n  \n{/${variable}}`,
            falsyText: `{^${variable}}\n  \n{/${variable}}`,
        };
    }
    const tripleMatch = field.match(/^\{\{\{(.+?)\}\}\}$/);
    if (tripleMatch) {
        return { kind: 'html', text: `{~~${tripleMatch[1].trim()}}` };
    }
    const doubleMatch = field.match(/^\{\{(.+?)\}\}$/);
    if (doubleMatch) {
        return { kind: 'plain', text: `{${doubleMatch[1].trim()}}` };
    }
    return { kind: 'plain', text: field };
};
const DocxTemplateSection = (props) => {
    const lang = useSelector((s) => s.lang);
    const docxDropzoneRef = useRef(null);
    const [docxUploading, setDocxUploading] = useState(false);
    const tagRows = [
        {
            key: '__body__',
            kind: 'body',
            description: lang.templates.docxBodyTagDescription,
            field: HTML_BODY_TAG,
            example: '—',
        },
        ...props.mentionSuggestions
            // Page-break is a print-only directive that has no meaning in docx;
            // hide it from the docx tag list.
            .filter((suggestion) => ![
            TemplateMention.PAGE_BREAK,
            TemplateMention.DK_BPK,
            TemplateMention.GL_BPK,
            TemplateMention.PAYMENT_LINK_QR_CODE,
            TemplateMention.DEBTOR_WEB_LINK_QR_CODE,
            TemplateMention.OVERPAYMENT_LINK_QR_CODE,
            TemplateMention.PAYMENT_PLAN_LINK_QR_CODE,
        ].includes(suggestion.field))
            .map((suggestion) => {
            const rendered = renderTagFromField(suggestion.field);
            return {
                key: suggestion.field,
                kind: rendered.kind,
                description: suggestion.description,
                field: suggestion.field,
                example: suggestion.example,
                rendered,
            };
        }),
    ];
    // Upload only changes the in-memory template; the new file is persisted
    // when the user saves a draft or publishes from the editor's Header.
    const onDocxUploaded = (file) => {
        const docxFile = {
            fileId: file.fileId,
            fileName: file.fileName ?? 'template.docx',
        };
        props.setTemplate({ ...props.template, docxFile });
        successNotification(lang, 'create', lang.templates.docxTemplateUploaded);
    };
    const removeDocxTemplate = () => {
        props.setTemplate({ ...props.template, docxFile: null });
    };
    return (React.createElement(Panel, { header: lang.templates.docxTemplate },
        React.createElement("p", null, lang.templates.docxTemplateDescription),
        React.createElement(FileDropzone, { dropzoneRef: docxDropzoneRef, accept: `.docx,${FileMimeType.docx}`, setLoading: setDocxUploading, onChange: onDocxUploaded },
            React.createElement(DocxDropArea, null,
                props.template.docxFile != null ? (React.createElement(DocxFileLine, null, props.template.docxFile.fileName)) : (React.createElement(DocxHint, null, lang.inputs.dropToUpload)),
                React.createElement(DocxActions, null,
                    React.createElement(Button, { size: "small", theme: "secondary", disabled: docxUploading, onClick: () => {
                            docxDropzoneRef.current?.open?.();
                        } }, lang.templates.uploadDocxTemplate),
                    props.template.docxFile != null && (React.createElement(Button, { size: "small", theme: "dangerous", disabled: docxUploading, onClick: removeDocxTemplate }, lang.templates.removeDocxTemplate))))),
        React.createElement("h4", null, lang.templates.docxAvailableTags),
        React.createElement("p", null, lang.templates.docxAvailableTagsDescription),
        React.createElement(PowerTable, { data: tagRows, genKey: (row) => row.key, searchable: true, 
            // Only the description: the shared search() ranks longer strings
            // higher (its indexScore divides by total window count), so combining
            // description + field would push verbose rows above shorter, more
            // specific matches.
            toSearchStr: (row) => row.description, pagination: { type: 'none', pageSize: tagRows.length }, values: {
                description: {
                    label: lang.interactions.explanation,
                    sortable: false,
                    map: (_value, row) => row.kind === 'conditional' ? (React.createElement("div", null,
                        React.createElement("p", null,
                            React.createElement("b", null, row.description)),
                        lang.templates.docxConditionalDescription)) : (React.createElement("b", null, row.description)),
                },
                field: {
                    label: lang.templates.docxTag,
                    sortable: false,
                    map: (_value, row) => {
                        if (row.kind === 'body') {
                            return (React.createElement(TagCell, null,
                                React.createElement(CopyToClipboard, { text: HTML_BODY_TAG },
                                    React.createElement(TagCode, null, HTML_BODY_TAG))));
                        }
                        if (row.rendered?.kind === 'conditional') {
                            return (React.createElement(ConditionalCell, null,
                                React.createElement(KindBadge, { $kind: "conditional" }, lang.templates.docxConditionalBadge),
                                React.createElement(ConditionalBlock, null,
                                    React.createElement(ConditionalBlockLabel, null, lang.templates.docxConditionalIfTrue),
                                    React.createElement(CopyToClipboard, { text: row.rendered.truthyText },
                                        React.createElement(TagCode, { $multiline: true }, row.rendered.truthyText))),
                                React.createElement(ConditionalBlock, null,
                                    React.createElement(ConditionalBlockLabel, null, lang.templates.docxConditionalIfFalse),
                                    React.createElement(CopyToClipboard, { text: row.rendered.falsyText },
                                        React.createElement(TagCode, { $multiline: true }, row.rendered.falsyText)))));
                        }
                        const text = row.rendered?.kind === 'plain' || row.rendered?.kind === 'html'
                            ? row.rendered.text
                            : row.field;
                        return (React.createElement(TagCell, null,
                            React.createElement(CopyToClipboard, { text: text },
                                React.createElement(TagCode, null, text))));
                    },
                },
                example: {
                    label: lang.interactions.example,
                    sortable: false,
                    map: (_value, row) => row.kind === 'plain' || row.kind === 'html' ? row.example : '—',
                },
            } })));
};
export default DocxTemplateSection;
const DocxDropArea = styled.div `
  border: 1px dashed ${THEME.COLOR_GREY};
  border-radius: 4px;
  padding: 1em;
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  margin-bottom: 1em;
`;
const DocxFileLine = styled.div `
  font-weight: 600;
`;
const DocxHint = styled.div `
  color: ${THEME.COLOR_GREY};
  font-size: 0.9em;
`;
const DocxActions = styled.div `
  display: flex;
  gap: 0.5em;
`;
const TagCell = styled.div `
  display: inline-flex;
  align-items: flex-start;
  gap: 0.5em;
  flex-wrap: wrap;
`;
const TagCode = styled.code `
  background: #f3f1ff;
  color: ${THEME.COLOR_PRIMARY};
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  ${(p) => p.$multiline &&
    `
      white-space: pre;
      display: inline-block;
      line-height: 1.4;
    `}
`;
const KindBadge = styled.span `
  display: inline-block;
  font-size: 0.7em;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${(p) => (p.$kind === 'html' ? '#fff2cc' : '#d6e9ff')};
  color: ${(p) => (p.$kind === 'html' ? '#7a5c00' : '#1f4f87')};
`;
const ConditionalCell = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;
const ConditionalBlock = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.25em;
`;
const ConditionalBlockLabel = styled.div `
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${THEME.COLOR_GREY};
`;
//# sourceMappingURL=DocxTemplateSection.js.map